import StickyTableResponsive from '../../../components/common/StickyTableResponsive';
import SortableTh from '../../../components/common/SortableTh';
import React, { useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import { Link } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import { AppBadge, AppButton, AppEmptyState, AppSpinner } from '../../../components/ui';
import KkpColorBadge from '../../../components/kkp/KkpColorBadge';
import secteursAdminService from '../../../services/secteursAdminService';
import { notify } from '../../../components/common/AppToast';
import secteursService from '../../../services/secteursService';
import { formatDashboardCa } from '../../../utils/dashboardV2Utils';
import { buildCommercialColorByName, getCommercialColor } from '../../../utils/commercialBadgeUtils';
import { ROUTES } from '../../../config/routes';

const TABLE_SORT_COLUMNS = {
  id: { type: 'number', getValue: (row) => row.id },
  client: { type: 'string', getValue: (row) => row.tier_nom },
  date: { type: 'date', getValue: (row) => row.date_commande },
  totalHt: { type: 'number', getValue: (row) => row.total_ht ?? 0 },
  totalTtc: { type: 'number', getValue: (row) => row.total_ttc ?? 0 },
  statut: { type: 'string', getValue: (row) => row.statut },
  commercial: { type: 'string', getValue: (row) => row.commercial?.nom || row.commercial?.email },
};

const SecteursAdminCommandesPage = () => {
  const [commandes, setCommandes] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const colorByName = useMemo(() => buildCommercialColorByName(commerciaux), [commerciaux]);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(commandes, TABLE_SORT_COLUMNS);

  useEffect(() => {
    secteursService.getCommerciaux()
      .then(setCommerciaux)
      .catch(() => setCommerciaux([]));
  }, []);

  useEffect(() => {
    secteursAdminService.getCommandes(100)
      .then((result) => setCommandes(result.commandes))
      .catch((err) => notify.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <MasterLayout>
      <Breadcrumb
        title="Commandes Dolibarr"
        subtitle="Dernières commandes synchronisées"
        actions={(
          <AppButton as={Link} to={ROUTES.secteurs.admin} size="sm" variant="outline-secondary">Retour</AppButton>
        )}
      />

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : commandes.length === 0 ? (
            <AppEmptyState message="Aucune commande" />
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <SortableTh sortKey="id" label="ID" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="client" label="Client" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="date" label="Date" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="totalHt" label="Total HT" className="text-end" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="totalTtc" label="Total TTC" className="text-end" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((c) => (
                    <tr key={c.id}>
                      <td><code>{c.id}</code></td>
                      <td>{c.tier_nom}</td>
                      <td>{c.date_commande || '—'}</td>
                      <td className="text-end">{formatDashboardCa(c.total_ht)}</td>
                      <td className="text-end">{formatDashboardCa(c.total_ttc)}</td>
                      <td><AppBadge variant="secondary">{c.statut}</AppBadge></td>
                      <td>
                        {c.commercial?.nom || c.commercial?.email ? (
                          <KkpColorBadge
                            color={getCommercialColor(colorByName, c.commercial.nom) ?? getCommercialColor(colorByName, c.commercial.email)}
                            vivid
                          >
                            {c.commercial.nom || c.commercial.email}
                          </KkpColorBadge>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default SecteursAdminCommandesPage;
